// Copyright (c) 2025 by Audere

import { Box, Typography, capitalize } from "@mui/material";
import {
  GetConversationResponse,
  LabelerConversationData,
  QCConversationData,
} from "../features/api/labelApiSlice";
import { formatTimestamp } from "../utils/dates";
import ConversationsList from "./ConversationsList";
import { priorityChipStyles } from "./SmallChip";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useLocation, useParams } from "react-router-dom";
import ClientContext from "./ClientContext";

interface SidebarProps {
  isLoading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
  conversations: LabelerConversationData[] | QCConversationData[];
  onLoadMore: (value: "next" | "prev") => void;
  hasMore: boolean;
  endOfList: boolean;
  conversationMessages?: GetConversationResponse;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isLoading,
  error,
  conversations,
  onLoadMore,
  hasMore,
  endOfList,
  conversationMessages,
}) => {
  const { conversationUID } = useParams();
  const isEditMode = useLocation().pathname.endsWith("/edit");
  const conversationDetails =
    conversations.find((c) => c.conversation_uid === conversationUID) ??
    conversationMessages;

  return (
    <Box sx={styles.sidebar}>
      {!isEditMode && (
        <ConversationsList
          isLoading={isLoading}
          error={error}
          conversations={conversations}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          endOfList={endOfList}
        />
      )}

      {isEditMode && conversationDetails && (
        <>
          <Typography variant="body1">
            {conversationDetails.conversation_uid}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {formatTimestamp(conversationDetails.start_timestamp)}
          </Typography>

          <Typography
            variant="body2"
            aria-label={`Priority: ${capitalize(conversationDetails.priority)}`}
          >
            Priority:{" "}
            <span
              style={{
                color: priorityChipStyles[conversationDetails.priority].color,
              }}
            >
              {capitalize(conversationDetails.priority)}
            </span>
          </Typography>

          <ClientContext persona={conversationMessages?.persona} />
        </>
      )}
    </Box>
  );
};

const styles = createStyleSheet({
  sidebar: {
    ...globalStyles.flexColumn,
    borderRight: "1px solid lightgrey",
    flexBasis: "25%",
    padding: 2,
  },
});

export default Sidebar;
